import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Upload, Search } from "lucide-react";
import { api } from "../../lib/api";
import type { CalcInputPayload } from "@finora/api-client";
import { fmt } from "./shared";

export type InvoiceDraft = CalcInputPayload & {
  issueDate?: string | null;
  supplier?: { id: string; name: string; taxId: string | null };
};

/**
 * Polje „Broj fakture": pretraga ulaznih faktura iz SEF-a (broj | firma | iznos).
 * Izbor povlači stavke fakture i popunjava kalkulaciju.
 */
export function InvoicePicker({
  value,
  disabled,
  onPick,
}: {
  value: string;
  disabled?: boolean;
  onPick: (draft: InvoiceDraft) => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState(value);
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const file = useRef<HTMLInputElement>(null);

  useEffect(() => setText(value), [value]);
  useEffect(() => {
    const t = setTimeout(() => setTerm(text), 250);
    return () => clearTimeout(t);
  }, [text]);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const list = useQuery({
    queryKey: ["sef-purchase", term],
    queryFn: () => api.sefPurchaseSearch(term),
    enabled: open,
  });

  async function pick(id: string) {
    setBusy("Povlačim stavke fakture…");
    setMsg(null);
    try {
      const draft = (await api.sefPurchaseDraft(id)) as InvoiceDraft;
      onPick(draft);
      setOpen(false);
      setMsg({ ok: true, text: `Povučeno ${draft.lines?.length ?? 0} stavki iz fakture.` });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : "Greška pri povlačenju fakture." });
    } finally {
      setBusy(null);
    }
  }

  async function refresh() {
    setBusy("Osvežavam iz SEF-a…");
    setMsg(null);
    try {
      const r = await api.sefPurchaseSync();
      setMsg({
        ok: true,
        text: `SEF: ${r.imported} novih faktura${r.remaining ? `, još ${r.remaining} čeka (osvežite ponovo)` : ""}${r.failed ? `, ${r.failed} nije uspelo` : ""}.`,
      });
      await qc.invalidateQueries({ queryKey: ["sef-purchase"] });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : "SEF nije dostupan." });
    } finally {
      setBusy(null);
    }
  }

  async function onFile(f: File | undefined) {
    if (!f) return;
    setBusy("Učitavam XML…");
    setMsg(null);
    try {
      const inv = await api.sefPurchaseImportXml(await f.text());
      await qc.invalidateQueries({ queryKey: ["sef-purchase"] });
      await pick(inv.id);
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : "XML nije ispravan." });
      setBusy(null);
    }
    if (file.current) file.current.value = "";
  }

  return (
    <div ref={box} className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" aria-hidden />
          <input
            disabled={disabled}
            value={text}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setText(e.target.value);
              setOpen(true);
            }}
            placeholder="Broj fakture – kliknite za izbor iz SEF-a"
            className="h-10 w-full rounded-lg border border-border pl-9 pr-3 text-sm focus:border-brand focus:outline-none disabled:bg-slate-50"
          />
        </div>
        {!disabled && (
          <>
            <button type="button" onClick={refresh} disabled={!!busy} className="flex h-10 items-center gap-1 rounded-lg border border-border px-3 text-sm hover:bg-slate-50 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${busy?.startsWith("Osvež") ? "animate-spin" : ""}`} aria-hidden /> Osveži iz SEF-a
            </button>
            <button type="button" onClick={() => file.current?.click()} disabled={!!busy} className="flex h-10 items-center gap-1 rounded-lg border border-border px-3 text-sm hover:bg-slate-50 disabled:opacity-50">
              <Upload className="h-4 w-4" aria-hidden /> Učitaj XML
            </button>
            <input ref={file} type="file" accept=".xml,text/xml,application/xml" className="hidden" onChange={(e) => void onFile(e.target.files?.[0])} />
          </>
        )}
      </div>
      {busy && <div className="mt-1 text-xs text-slate-500">{busy}</div>}
      {msg && <div className={`mt-1 text-xs ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.text}</div>}

      {open && !disabled && (
        <div className="absolute z-30 mt-1 max-h-80 w-full overflow-auto rounded-lg border border-border bg-surface shadow-lg">
          {list.isLoading ? (
            <div className="p-3 text-sm text-slate-500">Učitavanje…</div>
          ) : !list.data?.length ? (
            <div className="p-3 text-sm text-slate-500">
              Nema faktura u kešu. Kliknite „Osveži iz SEF-a” ili „Učitaj XML”.
            </div>
          ) : (
            list.data.map((inv) => (
              <button
                key={inv.id}
                type="button"
                onClick={() => void pick(inv.id)}
                className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left text-sm last:border-0 hover:bg-slate-50"
              >
                <span className="truncate">
                  <span className="font-medium text-slate-900">{inv.number}</span>
                  <span className="text-slate-400"> | </span>
                  <span className="text-slate-700">{inv.supplierName}</span>
                  {inv.issueDate && <span className="text-slate-400"> · {new Date(inv.issueDate).toLocaleDateString("sr-RS")}</span>}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {inv.alreadyUsed && <span className="rounded bg-amber-100 px-1.5 text-[10px] text-amber-700">već u kalkulaciji</span>}
                  <span className="text-slate-600">{fmt(Number(inv.grossAmount))} {inv.currency}</span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
